import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ExternalLink, Loader2, LogOut, Upload, Mail, Trash2 } from "lucide-react";
import { useContentRefresh } from "@/context/ContentContext";
import { supabase } from "@/lib/supabase";
import { DEFAULT_CONTENT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESUME_BUCKET = "resume";
const authCfg = { withCredentials: true };

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) =>
                e && typeof e.msg === "string" ? e.msg : JSON.stringify(e),
            )
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const label = "mono text-[10px] tracking-[0.25em] text-muted2";
const input =
    "mt-2 w-full bg-transparent border-b border-line focus:border-ink outline-none py-2.5 text-sm transition-colors";

function slugify(s) {
    return s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

export default function Admin() {
    const navigate = useNavigate();
    const refresh = useContentRefresh();
    const [checking, setChecking] = useState(true);
    const [tab, setTab] = useState("content");
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [resumeStatus, setResumeStatus] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [messages, setMessages] = useState([]);
const [messagesLoading, setMessagesLoading] = useState(false);
const [certifications, setCertifications] = useState([]);
const [certificationsLoading, setCertificationsLoading] = useState(false);
const [certUploading, setCertUploading] = useState(false);
const [selectedCertFile, setSelectedCertFile] = useState(null);
const [certForm, setCertForm] = useState({
    title: "",
    issuer: "",
    issue_date: "",
    credential_id: "",
    credential_url: "",
    description: "",
});

const [pageExperiences, setPageExperiences] = useState([]);
const [pageExperiencesLoading, setPageExperiencesLoading] = useState(false);
const [experienceSaving, setExperienceSaving] = useState(false);
const [editingExperienceId, setEditingExperienceId] = useState(null);

const emptyExperienceForm = {
    role: "",
    org: "",
    location: "",
    period: "",
    bulletsText: "",
    stats: [
        { value: "", label: "" },
        { value: "", label: "" },
        { value: "", label: "" },
    ],
};

const [experienceForm, setExperienceForm] = useState(emptyExperienceForm);

   useEffect(() => {
    const loadAdmin = async () => {
        try {
            const {
                data: { session },
                error: sessionError,
            } = await supabase.auth.getSession();

            if (sessionError) throw sessionError;

            if (!session) {
                navigate("/login");
                return;
            }

            const { data, error } = await supabase
                .from("portfolio_content")
                .select("content")
                .eq("id", 1)
                .single();

            if (error) throw error;

            setDraft({
                ...DEFAULT_CONTENT,
                ...(data?.content || {}),
            });
        } catch (error) {
            console.error("Could not open admin:", error.message);
            toast.error("Could not load admin content. Please try again.");
        } finally {
            setChecking(false);
        }
    };

    loadAdmin();
}, [navigate]);

useEffect(() => {
    if (tab !== "resume" || checking) return;

    const loadResumeStatus = async () => {
        const { data, error } = await supabase.storage
            .from(RESUME_BUCKET)
            .list("", { search: "resume.pdf" });

        if (error) {
            toast.error("Could not load resume status.");
            return;
        }

        const exists = data?.some((file) => file.name === "resume.pdf");

        setResumeStatus({
            custom: Boolean(exists),
            filename: exists ? "resume.pdf" : "No resume uploaded",
        });
    };

    loadResumeStatus();
}, [tab, checking]);

useEffect(() => {
    if (tab !== "messages" || checking) return;

    const loadMessages = async () => {
        setMessagesLoading(true);

        const { data, error } = await supabase
            .from("messages")
            .select("*")
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Failed to load messages:", error.message);
            toast.error("Could not load messages.");
        } else {
            setMessages(data || []);
        }

        setMessagesLoading(false);
    };

    loadMessages();
}, [tab, checking]);
useEffect(() => {
    if (tab !== "certifications" || checking) return;

    const loadCertifications = async () => {
        setCertificationsLoading(true);

        const { data, error } = await supabase
            .from("certifications")
            .select("*")
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Failed to load certifications:", error.message);
            toast.error("Could not load certifications.");
        } else {
            setCertifications(data || []);
        }

        setCertificationsLoading(false);
    };

    loadCertifications();
}, [tab, checking]);

useEffect(() => {
    if (tab !== "experience" || checking) return;

    const loadPageExperiences = async () => {
        setPageExperiencesLoading(true);

        const { data, error } = await supabase
            .from("experience_page_entries")
            .select("*")
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: true });

        if (error) {
            console.error("Failed to load page experiences:", error.message);
            toast.error("Could not load Experience entries.");
        } else {
            setPageExperiences(data || []);
        }

        setPageExperiencesLoading(false);
    };

    loadPageExperiences();
}, [tab, checking]);

    if (checking || !draft) {
        return (
            <div className="min-h-screen bg-paper text-ink grid place-items-center">
                <p className="mono text-xs tracking-[0.25em] text-muted2">
                    OPENING THE STUDIO…
                </p>
            </div>
        );
    }

    const set = (path) => {
        setDraft((d) => ({ ...d, ...path }));
    };




   
    const toggleMessageRead = async (message) => {
        const nextReadState = !message.is_read;

        const { error } = await supabase
            .from("messages")
            .update({ is_read: nextReadState })
            .eq("id", message.id);

        if (error) {
            toast.error("Could not update message.");
            return;
        }

        setMessages((current) =>
            current.map((item) =>
                item.id === message.id
                    ? { ...item, is_read: nextReadState }
                    : item
            )
        );
    };

    const deleteMessage = async (id) => {
        const { error } = await supabase
            .from("messages")
            .delete()
            .eq("id", id);

        if (error) {
            toast.error("Could not delete message.");
            return;
        }

        setMessages((current) => current.filter((item) => item.id !== id));
        toast.success("Message deleted.");
    };
const uploadCertification = async () => {
    const { title, issuer, issue_date, credential_id, credential_url, description } = certForm;
    const file = selectedCertFile;

    if (!title.trim() || !issuer.trim() || !file) {
        toast.error("Please enter the title, issuer and select a certificate file.");
        return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
        toast.error("Only PDF, JPG and PNG files are allowed.");
        return;
    }

    if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be under 10 MB.");
        return;
    }

    setCertUploading(true);

    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const filePath = `${Date.now()}-${safeName}`;

    try {
        const { error: uploadError } = await supabase.storage
            .from("certifications")
            .upload(filePath, file, {
                contentType: file.type,
                upsert: false,
            });

        if (uploadError) throw uploadError;

        const { data, error: insertError } = await supabase
            .from("certifications")
            .insert([{
                title: title.trim(),
                issuer: issuer.trim(),
                issue_date: issue_date || null,
                credential_id: credential_id.trim() || null,
                credential_url: credential_url.trim() || null,
                description: description.trim() || null,
                file_path: filePath,
                file_type: file.type,
            }])
            .select()
            .single();

        if (insertError) {
            await supabase.storage.from("certifications").remove([filePath]);
            throw insertError;
        }

        setCertifications((current) => [data, ...current]);
        setCertForm({
            title: "",
            issuer: "",
            issue_date: "",
            credential_id: "",
            credential_url: "",
            description: "",
        });
        setSelectedCertFile(null);

        const fileInput = document.getElementById("certification-file");
        if (fileInput) fileInput.value = "";

        toast.success("Certification uploaded successfully.");
    } catch (error) {
        console.error("Certification upload failed:", error);
        toast.error(error.message || "Could not upload certification.");
    } finally {
        setCertUploading(false);
    }
};

const deleteUploadedCertification = async (certification) =>{
    const { error: deleteError } = await supabase
        .from("certifications")
        .delete()
        .eq("id", certification.id);

    if (deleteError) {
        toast.error("Could not delete certification.");
        return;
    }

    const { error: fileError } = await supabase.storage
        .from("certifications")
        .remove([certification.file_path]);

    if (fileError) {
        console.error("Certificate file deletion failed:", fileError);
    }

    setCertifications((current) =>
        current.filter((item) => item.id !== certification.id)
    );

    toast.success("Certification deleted.");
};

const savePageExperience = async () => {
const { role, org, location, period, bulletsText, stats } = experienceForm;
    if (!role.trim() || !org.trim()) {
        toast.error("Please enter the role and company.");
        return;
    }

    const bullets = bulletsText
        .split("\n")
        .map((bullet) => bullet.trim())
        .filter(Boolean);

    setExperienceSaving(true);

const experienceData = {
    role: role.trim(),
    org: org.trim(),
    location: location.trim() || null,
    period: period.trim() || null,
    bullets,
    stats: stats.map((stat) => ({
        value: stat.value.trim(),
        label: stat.label.trim(),
    })),
};

    try {
        if (editingExperienceId) {
            const { data, error } = await supabase
                .from("experience_page_entries")
                .update(experienceData)
                .eq("id", editingExperienceId)
                .select()
                .single();

            if (error) throw error;

            setPageExperiences((current) =>
                current.map((item) =>
                    item.id === editingExperienceId ? data : item
                )
            );

            toast.success("Experience updated.");
        } else {
            const { data, error } = await supabase
                .from("experience_page_entries")
                .insert([{
                    ...experienceData,
                    sort_order: pageExperiences.length,
                }])
                .select()
                .single();

            if (error) throw error;

            setPageExperiences((current) => [...current, data]);
            toast.success("Experience added.");
        }

        setExperienceForm(emptyExperienceForm);
        setEditingExperienceId(null);
    } catch (error) {
        console.error("Experience save failed:", error);
        toast.error(error.message || "Could not save Experience.");
    } finally {
        setExperienceSaving(false);
    }
};

const editPageExperience = (experience) => {
    setEditingExperienceId(experience.id);

    setExperienceForm({
        role: experience.role || "",
        org: experience.org || "",
        location: experience.location || "",
        period: experience.period || "",
        bulletsText: (experience.bullets || []).join("\n"),
        stats: experience.stats?.length === 3
            ? experience.stats
            : [
                { value: "", label: "" },
                { value: "", label: "" },
                { value: "", label: "" },
            ],
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
};

const deletePageExperience = async (experience) => {
    const { error } = await supabase
        .from("experience_page_entries")
        .delete()
        .eq("id", experience.id);

    if (error) {
        toast.error("Could not delete Experience entry.");
        return;
    }

    setPageExperiences((current) =>
        current.filter((item) => item.id !== experience.id)
    );

    if (editingExperienceId === experience.id) {
        setExperienceForm(emptyExperienceForm);
        setEditingExperienceId(null);
    }

    toast.success("Experience deleted.");
};
    const saveAll = async () => {
        setSaving(true);

        try {
            const { error } = await supabase
                .from("portfolio_content")
                .update({
                    content: draft,
                    updated_at: new Date().toISOString(),
                })
                .eq("id", 1);

            if (error) throw error;

            await refresh();
            toast.success("Site content saved successfully.");
        } catch (error) {
            console.error("Could not save portfolio:", error.message);
            toast.error(`Could not save changes: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };


 
    const logout = async () => {
        const { error } = await supabase.auth.signOut();

        if (error) {
            toast.error(`Logout failed: ${error.message}`);
            return;
        }

        navigate("/login");
    };


    // ---- projects ----
    const updateProject = (idx, field, value) => {
        set({
            projects: draft.projects.map((p, i) =>
                i === idx ? { ...p, [field]: value } : p,
            ),
        });
    };

    const addProject = () => {
        set({
            projects: [
                ...draft.projects,
                {
                    slug: `project-${slugify(String(draft.projects.length + 1))}-${Date.now() % 1000}`,
                    title: "New Project",
                    subtitle: "",
                    category: "Web Development",
                    tags: [],
                    image: "",
                    link: "",
                    show_link: false,
                },
            ],
        });
    };

    const deleteProject = (idx) => {
        if (window.confirm(`Delete "${draft.projects[idx].title}"? This can't be undone.`)) {
            set({ projects: draft.projects.filter((_, i) => i !== idx) });
        }
    };

    // ---- experiences ----
    const updateExperience = (idx, field, value) => {
        set({
            experiences: (draft.experiences || []).map((e, i) =>
                i === idx ? { ...e, [field]: value } : e,
            ),
        });
    };

    const addExperience = () => {
        set({
            experiences: [
                ...(draft.experiences || []),
                { role: "", org: "", location: "", period: "", bullets: [] },
            ],
        });
    };

    const deleteExperience = (idx) => {
        const entry = draft.experiences[idx];
        if (window.confirm(`Delete experience "${entry.role || "Untitled"}"?`)) {
            set({ experiences: draft.experiences.filter((_, i) => i !== idx) });
        }
    };



const uploadResume = async (e) => {
    const file = e.target.files?.[0];
const { data: { session }, error: sessionError } =
    await supabase.auth.getSession();

let tokenRole = null;
try {
    if (session?.access_token) {
        tokenRole = JSON.parse(
            atob(session.access_token.split(".")[1])
        ).role;
    }
} catch {}

console.log("Resume upload session check:", {
    userId: session?.user?.id,
    tokenRole,
    hasAccessToken: !!session?.access_token,
    sessionError: sessionError?.message
});
    if (!file) return;

    if (file.type !== "application/pdf") {
        toast.error("Please select a PDF file.");
        e.target.value = "";
        return;
    }

    setUploading(true);

    try {
        const { error } = await supabase.storage
            .from(RESUME_BUCKET)
            .upload("resume.pdf", file, {
                contentType: "application/pdf",
                upsert: true,
            });

        if (error) throw error;

        setResumeStatus({
            custom: true,
            filename: "resume.pdf",
        });

        toast.success("Resume uploaded successfully.");
    } catch (err) {
        console.error("Resume upload failed:", err);
        toast.error(err.message || "Resume upload failed.");
    } finally {
        setUploading(false);
        e.target.value = "";
    }
};

const TABS = [
    ["content", "CONTENT"],
    ["projects", "PROJECTS"],
    ["resume", "RESUME"],
    ["messages", "MESSAGES"],
    ["certifications", "CERTIFICATIONS"],
    ["experience", "EXPERIENCE"],
];

    return (
        <div className="min-h-screen bg-paper text-ink" data-testid="admin-page">
            <header className="sticky top-0 z-40 bg-ink text-paper px-5 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-4">
                <Link to="/" data-testid="admin-wordmark" className="mono font-bold text-sm">
                    _beingdevang<span className="blink text-sage">_</span>
                </Link>
                <nav className="flex items-center gap-2" data-testid="admin-tabs">
                    {TABS.map(([id, lbl]) => (
                        <button
                            key={id}
                            data-testid={`admin-tab-${id}`}
                            onClick={() => setTab(id)}
                            className={`mono text-[10px] tracking-[0.2em] px-4 py-2 border transition-colors ${
                                tab === id
                                    ? "bg-sage text-ink border-sage"
                                    : "border-paper2/30 text-paper2/70 hover:text-paper hover:border-paper"
                            }`}
                        >
                            {lbl}
                        </button>
                    ))}
                </nav>
                <button
                    onClick={logout}
                    data-testid="admin-logout-button"
                    className="group mono text-[10px] tracking-[0.2em] inline-flex items-center gap-2 border border-paper2/30 px-4 py-2 hover:bg-paper hover:text-ink transition-colors"
                >
                    LOG OUT
                    <LogOut className="h-3.5 w-3.5" />
                </button>
            </header>

            <main className="px-5 lg:px-10 py-10 max-w-4xl mx-auto pb-24">
                {tab === "content" && (
                    <section data-testid="admin-content-tab">
                        <h1 className="font-display text-3xl tracking-tight">
                            EDIT CONTENT
                        </h1>
                        <p className="text-sm text-muted2 mt-2">
                            Every field below updates the live site the moment
                            you save.
                        </p>

                        <div className="mt-10 space-y-10">
                            <div>
                                <span className={label}>HERO BIO</span>
                                <textarea
                                    rows={3}
                                    value={draft.hero_bio || ""}
                                    onChange={(e) => set({ hero_bio: e.target.value })}
                                    data-testid="admin-hero-bio"
                                    className={input + " resize-none"}
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <span className={label}>ABOUT — PARAGRAPH 1</span>
                                    <textarea
                                        rows={4}
                                        value={draft.about_p1 || ""}
                                        onChange={(e) => set({ about_p1: e.target.value })}
                                        data-testid="admin-about-p1"
                                        className={input + " resize-none"}
                                    />
                                </div>
                                <div>
                                    <span className={label}>ABOUT — PARAGRAPH 2</span>
                                    <textarea
                                        rows={4}
                                        value={draft.about_p2 || ""}
                                        onChange={(e) => set({ about_p2: e.target.value })}
                                        data-testid="admin-about-p2"
                                        className={input + " resize-none"}
                                    />
                                </div>
                            </div>
                            <div>
                                <span className={label}>GOALS — ONE PER LINE</span>
                                <textarea
                                    rows={4}
                                    value={(draft.goals || []).join("\n")}
                                    onChange={(e) =>
                                        set({
                                            goals: e.target.value
                                                .split("\n")
                                                .map((s) => s.trim())
                                                .filter(Boolean),
                                        })
                                    }
                                    data-testid="admin-goals"
                                    className={input + " resize-none"}
                                />
                            </div>
                            <div>
                                <span className={label}>
                                    SKILLS — ONE GROUP PER LINE: GROUP | ITEM, ITEM
                                </span>
                                <textarea
                                    rows={4}
                                    value={(draft.skills || [])
                                        .map((g) => `${g.label} | ${g.items.join(", ")}`)
                                        .join("\n")}
                                    onChange={(e) =>
                                        set({
                                            skills: e.target.value
                                                .split("\n")
                                                .filter((l) => l.trim())
                                                .map((l) => {
                                                    const [lbl, items] = l.split("|");
                                                    return {
                                                        label: (lbl || "").trim(),
                                                        items: (items || "")
                                                            .split(",")
                                                            .map((s) => s.trim())
                                                            .filter(Boolean),
                                                    };
                                                }),
                                        })
                                    }
                                    data-testid="admin-skills"
                                    className={input + " resize-none"}
                                />
                            </div>

                            <div className="border-t border-line pt-8">
                                <div className="flex items-center justify-between">
                                    <p className={label}>EXPERIENCE ENTRIES</p>
                                    <button
                                        onClick={addExperience}
                                        data-testid="admin-add-experience-button"
                                        className="mono text-[10px] tracking-[0.2em] border border-ink px-3 py-1.5 hover:bg-ink hover:text-paper transition-colors"
                                    >
                                        + ADD ENTRY
                                    </button>
                                </div>
                                <div className="mt-6 space-y-8">
                                    {(draft.experiences || []).map((e, ei) => (
                                        <div
                                            key={ei}
                                            className="border border-line bg-paper2 p-6"
                                            data-testid={`admin-experience-${ei + 1}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                                    ENTRY 0{ei + 1} — {e.role || "UNTITLED"}
                                                </p>
                                                <button
                                                    onClick={() => deleteExperience(ei)}
                                                    data-testid={`admin-experience-delete-${ei + 1}`}
                                                    className="mono text-[10px] tracking-[0.2em] text-red-400 hover:text-red-600"
                                                >
                                                    DELETE
                                                </button>
                                            </div>
                                            <div className="grid md:grid-cols-2 gap-6 mt-5">
                                                <input
                                                    value={e.role}
                                                    onChange={(ev) => updateExperience(ei, "role", ev.target.value)}
                                                    placeholder="Role"
                                                    data-testid={`admin-exp-${ei + 1}-role`}
                                                    className={input}
                                                />
                                                <input
                                                    value={e.org}
                                                    onChange={(ev) => updateExperience(ei, "org", ev.target.value)}
                                                    placeholder="Organisation"
                                                    data-testid={`admin-exp-${ei + 1}-org`}
                                                    className={input}
                                                />
                                                <input
                                                    value={e.location}
                                                    onChange={(ev) => updateExperience(ei, "location", ev.target.value)}
                                                    placeholder="Location"
                                                    data-testid={`admin-exp-${ei + 1}-location`}
                                                    className={input}
                                                />
                                                <input
                                                    value={e.period}
                                                    onChange={(ev) => updateExperience(ei, "period", ev.target.value)}
                                                    placeholder="Period (e.g. May – Jul 2024)"
                                                    data-testid={`admin-exp-${ei + 1}-period`}
                                                    className={input}
                                                />
                                            </div>
                                            <div className="mt-6">
                                                <span className={label}>BULLETS — ONE PER LINE</span>
                                                <textarea
                                                    rows={3}
                                                    value={(e.bullets || []).join("\n")}
                                                    onChange={(ev) =>
                                                        updateExperience(
                                                            ei,
                                                            "bullets",
                                                            ev.target.value
                                                                .split("\n")
                                                                .map((s) => s.trim())
                                                                .filter(Boolean),
                                                        )
                                                    }
                                                    data-testid={`admin-exp-${ei + 1}-bullets`}
                                                    className={input + " resize-none"}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    {(draft.experiences || []).length === 0 && (
                                        <p className="mono text-sm text-muted2">
                                            No entries yet — add your first one.
                                        </p>
                                    )}
                                </div>
                            </div>

                           

                            <div className="border-t border-line pt-8">
                                <p className={label}>CONTACT DETAILS</p>
                                <div className="grid md:grid-cols-2 gap-6 mt-4">
                                    {[
                                        ["email", "Email"],
                                        ["linkedin_url", "LinkedIn URL"],
                                        ["linkedin_handle", "LinkedIn handle (shown)"],
                                        ["instagram_url", "Instagram URL"],
                                        ["instagram_handle", "Instagram handle (shown)"],
                                        ["location", "Location"],
                                    ].map(([key, ph]) => (
                                        <input
                                            key={key}
                                            value={draft.contact?.[key] || ""}
                                            onChange={(e) => set({ contact: { ...draft.contact, [key]: e.target.value } })}
                                            placeholder={ph}
                                            data-testid={`admin-contact-${key}`}
                                            className={input}
                                        />
                                ))}
                                </div>
                            </div>
                        </div>
                    </section>
                )}

                {tab === "projects" && (
                    <section data-testid="admin-projects-tab">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <h1 className="font-display text-3xl tracking-tight">
                                EDIT PROJECTS
                            </h1>
                            <button
                                onClick={addProject}
                                data-testid="admin-add-project-button"
                                className="mono text-[10px] tracking-[0.2em] border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors"
                            >
                                + ADD PROJECT
                            </button>
                        </div>
                        <p className="text-sm text-muted2 mt-2">
                            Each project can carry an external link — switch it
                            on or off per project.
                        </p>

                        <div className="mt-10 space-y-10">
                            {draft.projects.map((p, idx) => (
                                <div
                                    key={p.slug}
                                    className="border border-line bg-paper2 p-6"
                                    data-testid={`admin-project-${p.slug}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                            0{idx + 1} — {p.title}
                                        </p>
                                        <button
                                            onClick={() => deleteProject(idx)}
                                            data-testid={`project-delete-${p.slug}`}
                                            className="mono text-[10px] tracking-[0.2em] text-red-400 hover:text-red-600"
                                        >
                                            DELETE
                                        </button>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-6 mt-5">
                                        <input
                                            value={p.title}
                                            onChange={(e) => updateProject(idx, "title", e.target.value)}
                                            placeholder="Title"
                                            data-testid={`project-title-input-${p.slug}`}
                                            className={input}
                                        />
                                        <input
                                            value={p.subtitle}
                                            onChange={(e) => updateProject(idx, "subtitle", e.target.value)}
                                            placeholder="Subtitle"
                                            data-testid={`project-subtitle-input-${p.slug}`}
                                            className={input}
                                        />
                                        <input
                                            value={p.category}
                                            onChange={(e) => updateProject(idx, "category", e.target.value)}
                                            placeholder="Category (for filters)"
                                            data-testid={`project-category-input-${p.slug}`}
                                            className={input}
                                        />
                                        <input
                                            value={(p.tags || []).join(", ")}
                                            onChange={(e) =>
                                                updateProject(
                                                    idx,
                                                    "tags",
                                                    e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                                                )
                                            }
                                            placeholder="Tags, comma separated"
                                            data-testid={`project-tags-input-${p.slug}`}
                                            className={input}
                                        />
                                        <input
                                            value={p.image}
                                            onChange={(e) => updateProject(idx, "image", e.target.value)}
                                            placeholder="Image URL"
                                            data-testid={`project-image-input-${p.slug}`}
                                            className={input}
                                        />
                                        <input
                                            value={p.link || ""}
                                            onChange={(e) => updateProject(idx, "link", e.target.value)}
                                            placeholder="External link (https://…)"
                                            data-testid={`project-link-input-${p.slug}`}
                                            className={input}
                                        />
                                    </div>
                                    <label className="mt-5 inline-flex items-center gap-3 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={!!p.show_link}
                                            onChange={(e) => updateProject(idx, "show_link", e.target.checked)}
                                            data-testid={`project-link-toggle-${p.slug}`}
                                            className="h-4 w-4 accent-[#161A16]"
                                        />
                                        <span className="mono text-[10px] tracking-[0.2em] text-muted2">
                                            SHOW A LINK FOR THIS PROJECT
                                        </span>
                                        {p.show_link && p.link && (
                                            <a
                                                href={p.link}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-sage hover:text-ink"
                                            >
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                    </label>
                                    {p.image && (
                                        <img
                                            src={p.image}
                                            alt=""
                                            className="mt-5 w-40 aspect-[4/3] object-cover border border-line"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {tab === "resume" && (
                    <section data-testid="admin-resume-tab">
                        <h1 className="font-display text-3xl tracking-tight">
                            RESUME
                        </h1>
                        <p className="text-sm text-muted2 mt-2">
                            Upload your own PDF — every "Download Resume" button
                            on the site serves it instantly.
                        </p>

                        <div
                            className="mt-10 border border-line bg-paper2 p-8"
                            data-testid="resume-upload-card"
                        >
                            <p className="mono text-[10px] tracking-[0.25em] text-muted2">
                                CURRENT FILE
                            </p>
                            <p className="mt-2 text-lg" data-testid="resume-status">
                                {resumeStatus
                                    ? `${resumeStatus.custom ? "Custom upload" : "Placeholder"} — ${resumeStatus.filename}`
                                    : "Loading…"}
                            </p>
                            <a
                                href={supabase.storage.from(RESUME_BUCKET).getPublicUrl("resume.pdf").data.publicUrl}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="resume-preview-link"
                                className="mono text-[10px] tracking-[0.2em] inline-flex items-center gap-2 mt-3 border-b border-ink/40 pb-1 hover:border-ink"
                            >
                                OPEN CURRENT PDF
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>

                            <label className="mt-8 inline-flex">
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={uploadResume}
                                    data-testid="resume-file-input"
                                    className="hidden"
                                />
                                <span className="group cursor-pointer inline-flex items-center gap-3 bg-ink text-paper px-6 py-3 mono text-[11px] tracking-[0.25em] uppercase hover:bg-sage hover:text-ink transition-colors">
                                    {uploading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Upload className="h-4 w-4" />
                                    )}
                                    {uploading ? "UPLOADING…" : "UPLOAD PDF"}
                                </span>
                            </label>
                            <p className="mono text-[10px] tracking-[0.2em] text-muted2 mt-4">
                                PDF ONLY, UP TO 10 MB
                            </p>
                        </div>
                    </section>
             
                )}

                {tab === "messages" && (
                    <section data-testid="admin-messages-tab">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <h1 className="font-display text-3xl tracking-tight">
                                    MESSAGES
                                </h1>
                                <p className="text-sm text-muted2 mt-2">
                                    Messages submitted through your portfolio contact form.
                                </p>
                            </div>
                            <p className="mono text-[10px] tracking-[0.2em] text-muted2">
                                {messages.filter((m) => !m.is_read).length} UNREAD
                            </p>
                        </div>

                        {messagesLoading ? (
                            <div className="mt-10 flex items-center gap-3 text-muted2">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span className="mono text-xs">LOADING MESSAGES...</span>
                            </div>
                        ) : messages.length === 0 ? (
                            <div className="mt-10 border border-line bg-paper2 p-8">
                                <Mail className="h-6 w-6 mb-4 text-muted2" />
                                <p className="text-lg">Your inbox is empty.</p>
                                <p className="text-sm text-muted2 mt-2">
                                    New contact form submissions will appear here.
                                </p>
                            </div>
                        ) : (
                            <div className="mt-8 space-y-4">
                                {messages.map((message) => (
                                    <article
                                        key={message.id}
                                        className={`border border-line p-5 sm:p-6 ${
                                            message.is_read ? "bg-paper" : "bg-paper2"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h2 className="text-lg font-medium">
                                                        {message.name}
                                                    </h2>
                                                    {!message.is_read && (
                                                        <span className="mono text-[9px] tracking-[0.15em] bg-sage px-2 py-1">
                                                            UNREAD
                                                        </span>
                                                    )}
                                                </div>
                                                <a
                                                    href={`mailto:${message.email}`}
                                                    className="text-sm text-muted2 hover:text-ink break-all"
                                                >
                                                    {message.email}
                                                </a>
                                                <p className="mono text-[10px] text-muted2 mt-2">
                                                    {new Date(message.created_at).toLocaleString()}
                                                </p>
                                            </div>

                                            <div className="flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleMessageRead(message)}
                                                    className="inline-flex items-center gap-2 border border-line px-3 py-2 mono text-[10px] tracking-[0.1em] hover:bg-sage transition-colors"
                                                >
                                                    <Mail className="h-3.5 w-3.5" />
                                                    {message.is_read ? "MARK UNREAD" : "MARK READ"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (window.confirm("Delete this message permanently?")) {
                                                            deleteMessage(message.id);
                                                        }
                                                    }}
                                                    className="inline-flex items-center gap-2 border border-line px-3 py-2 mono text-[10px] tracking-[0.1em] hover:bg-red-100 transition-colors"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                    DELETE
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-5 border-t border-line pt-4">
                                            <h3 className="font-medium">{message.subject}</h3>
                                            <p className="mt-3 text-sm leading-7 whitespace-pre-wrap break-words">
                                                {message.message}
                                            </p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                )}
                {tab === "certifications" && (
                    <section data-testid="admin-certifications-tab">
                        <div className="mb-8">
                            <h1 className="font-display text-3xl tracking-tight">
                                CERTIFICATIONS
                            </h1>
                            <p className="text-sm text-muted2 mt-2">
                                Upload and manage your professional certificates.
                            </p>
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                uploadCertification();
                            }}
                            className="border border-line bg-paper2 p-6 space-y-5"
                        >
                            <h2 className="font-medium text-lg">ADD NEW CERTIFICATE</h2>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <input
                                    required
                                    placeholder="Certificate title *"
                                    value={certForm.title}
                                    onChange={(e) => setCertForm({ ...certForm, title: e.target.value })}
                                    className="border border-line bg-paper px-4 py-3 text-sm"
                                />
                                <input
                                    required
                                    placeholder="Issuing organisation *"
                                    value={certForm.issuer}
                                    onChange={(e) => setCertForm({ ...certForm, issuer: e.target.value })}
                                    className="border border-line bg-paper px-4 py-3 text-sm"
                                />
                                <input
                                    type="date"
                                    value={certForm.issue_date}
                                    onChange={(e) => setCertForm({ ...certForm, issue_date: e.target.value })}
                                    className="border border-line bg-paper px-4 py-3 text-sm"
                                />
                                <input
                                    placeholder="Credential ID (optional)"
                                    value={certForm.credential_id}
                                    onChange={(e) => setCertForm({ ...certForm, credential_id: e.target.value })}
                                    className="border border-line bg-paper px-4 py-3 text-sm"
                                />
                            </div>

                            <input
                                type="url"
                                placeholder="Credential verification URL (optional)"
                                value={certForm.credential_url}
                                onChange={(e) => setCertForm({ ...certForm, credential_url: e.target.value })}
                                className="w-full border border-line bg-paper px-4 py-3 text-sm"
                            />

                            <textarea
                                rows={3}
                                placeholder="Description (optional)"
                                value={certForm.description}
                                onChange={(e) => setCertForm({ ...certForm, description: e.target.value })}
                                className="w-full border border-line bg-paper px-4 py-3 text-sm"
                            />

                            <div>
                                <label className="block mono text-xs mb-2">
                                    CERTIFICATE FILE (PDF, JPG OR PNG, MAX 10 MB) *
                                </label>
                                <input
                                    id="certification-file"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                    required
                                    onChange={(e) => setSelectedCertFile(e.target.files?.[0] || null)}
                                    className="block w-full text-sm"
                                />
                                {selectedCertFile && (
                                    <p className="text-xs text-muted2 mt-2">
                                        Selected: {selectedCertFile.name}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={certUploading}
                                className="bg-ink text-paper px-6 py-3 mono text-xs tracking-widest disabled:opacity-50"
                            >
                                {certUploading ? "UPLOADING..." : "UPLOAD CERTIFICATE"}
                            </button>
                        </form>

                        <div className="mt-10">
                            <h2 className="font-display text-xl mb-5">
                                UPLOADED CERTIFICATES ({certifications.length})
                            </h2>

                            {certificationsLoading ? (
                                <p className="text-sm text-muted2">Loading certifications...</p>
                            ) : certifications.length === 0 ? (
                                <p className="text-sm text-muted2 border border-line p-6">
                                    No certificates uploaded yet.
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {certifications.map((certification) => {
                                        const fileUrl = supabase.storage
                                            .from("certifications")
                                            .getPublicUrl(certification.file_path).data.publicUrl;

                                        return (
                                            <article
                                                key={certification.id}
                                                className="border border-line p-5 flex flex-wrap items-start justify-between gap-4"
                                            >
                                                <div>
                                                    <h3 className="font-medium">{certification.title}</h3>
                                                    <p className="text-sm text-muted2 mt-1">
                                                        {certification.issuer}
                                                    </p>
                                                    {certification.issue_date && (
                                                        <p className="mono text-xs text-muted2 mt-2">
                                                            Issued: {certification.issue_date}
                                                        </p>
                                                    )}
                                                    {certification.credential_id && (
                                                        <p className="mono text-xs text-muted2 mt-1">
                                                            ID: {certification.credential_id}
                                                        </p>
                                                    )}
                                                    <a
                                                        href={fileUrl}

                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-block text-sm underline mt-3"
                                                    >
                                                        VIEW CERTIFICATE
                                                    </a>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (window.confirm("Delete this certificate permanently?")) {
                                                            deleteUploadedCertification(certification);
                                                        }
                                                    }}
                                                    className="border border-line px-3 py-2 mono text-xs hover:bg-red-100"
                                                >
                                                    DELETE
                                                </button>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </main>

{tab === "experience" && (
    <section data-testid="admin-experience-tab">
        <div className="mb-8">
            <h1 className="font-display text-3xl tracking-tight">
                EXPERIENCE
            </h1>
            <p className="text-sm text-muted2 mt-2">
                Manage the detailed Experience page independently from the homepage.
            </p>
        </div>

        <form
            onSubmit={(e) => {
                e.preventDefault();
                savePageExperience();
            }}
            className="border border-line bg-paper2 p-6 space-y-5"
        >
            <h2 className="font-medium text-lg">
                {editingExperienceId ? "EDIT EXPERIENCE" : "ADD NEW EXPERIENCE"}
            </h2>

<div className="grid sm:grid-cols-2 gap-4">
    <input
        required
        placeholder="Job title / Role *"
        value={experienceForm.role}
        onChange={(e) =>
            setExperienceForm({ ...experienceForm, role: e.target.value })
        }
        className="border border-line bg-paper px-4 py-3 text-sm"
    />

    <input
        required
        placeholder="Company / Organisation *"
        value={experienceForm.org}
        onChange={(e) =>
            setExperienceForm({ ...experienceForm, org: e.target.value })
        }
        className="border border-line bg-paper px-4 py-3 text-sm"
    />

    <input
        placeholder="Location"
        value={experienceForm.location}
        onChange={(e) =>
            setExperienceForm({ ...experienceForm, location: e.target.value })
        }
        className="border border-line bg-paper px-4 py-3 text-sm"
    />

    <input
        placeholder="Duration (e.g. Jun 2026 - Aug 2026)"
        value={experienceForm.period}
        onChange={(e) =>
            setExperienceForm({ ...experienceForm, period: e.target.value })
        }
        className="border border-line bg-paper px-4 py-3 text-sm"
    />
</div>

<div>
    <h3 className="mono text-xs tracking-widest mb-3">
        EXPERIENCE STATISTICS (3 BOXES)
    </h3>

    <div className="grid sm:grid-cols-3 gap-4">
        {experienceForm.stats.map((stat, index) => (
            <div key={index} className="border border-line p-4 space-y-3">
                <p className="mono text-[10px] tracking-widest text-muted2">
                    BOX 0{index + 1}
                </p>

                <input
                    placeholder="Value (e.g. 6000+)"
                    value={stat.value}
                    onChange={(e) => {
                        const updatedStats = [...experienceForm.stats];
                        updatedStats[index] = {
                            ...updatedStats[index],
                            value: e.target.value,
                        };
                        setExperienceForm({
                            ...experienceForm,
                            stats: updatedStats,
                        });
                    }}
                    className="w-full border border-line bg-paper px-3 py-3 text-sm"
                />

                <input
                    placeholder="Label (e.g. CALLS MADE)"
                    value={stat.label}
                    onChange={(e) => {
                        const updatedStats = [...experienceForm.stats];
                        updatedStats[index] = {
                            ...updatedStats[index],
                            label: e.target.value,
                        };
                        setExperienceForm({
                            ...experienceForm,
                            stats: updatedStats,
                        });
                    }}
                    className="w-full border border-line bg-paper px-3 py-3 text-sm"
                />
            </div>
        ))}
    </div>
</div><div>
    <label className="block mono text-xs mb-2">
        RESPONSIBILITIES (ONE PER LINE)
    </label>

    <textarea
        rows={6}
        placeholder={"Managed customer leads...\nMaintained follow-up records...\nSupported business development..."}
        value={experienceForm.bulletsText}
        onChange={(e) =>
            setExperienceForm({
                ...experienceForm,
                bulletsText: e.target.value,
            })
        }
        className="w-full border border-line bg-paper px-4 py-3 text-sm"
    />
</div>

            <div className="flex flex-wrap gap-3">
                <button
                    type="submit"
                    disabled={experienceSaving}
                    className="bg-ink text-paper px-6 py-3 mono text-xs tracking-widest disabled:opacity-50"
                >
                    {experienceSaving
                        ? "SAVING..."
                        : editingExperienceId
                            ? "UPDATE EXPERIENCE"
                            : "ADD EXPERIENCE"}
                </button>

                {editingExperienceId && (
                    <button
                        type="button"
                        onClick={() => {
                            setExperienceForm(emptyExperienceForm);
                            setEditingExperienceId(null);
                        }}
                        className="border border-line px-6 py-3 mono text-xs tracking-widest"
                    >
                        CANCEL EDIT
                    </button>
                )}
            </div>
        </form>

        <div className="mt-10">
            <h2 className="font-display text-xl mb-5">
                EXPERIENCE ENTRIES ({pageExperiences.length})
            </h2>

            {pageExperiencesLoading ? (
                <p className="text-sm text-muted2">Loading experiences...</p>
            ) : pageExperiences.length === 0 ? (
                <p className="text-sm text-muted2 border border-line p-6">
                    No Experience entries added yet.
                </p>
            ) : (
                <div className="space-y-4">
                    {pageExperiences.map((experience) => (
                        <article
                            key={experience.id}
                            className="border border-line p-5"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h3 className="font-medium text-lg">
                                        {experience.role}
                                    </h3>
                                    <p className="text-sm text-muted2 mt-1">
                                        {experience.org}
                                        {experience.location
                                            ? ` · ${experience.location}`
                                            : ""}
                                    </p>
                                    {experience.period && (
                                        <p className="mono text-xs text-muted2 mt-2">
                                            {experience.period}
                                        </p>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => editPageExperience(experience)}
                                        className="border border-line px-3 py-2 mono text-xs"
                                    >
                                        EDIT
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (window.confirm("Delete this Experience entry permanently?")) {
                                                deletePageExperience(experience);
                                            }
                                        }}
                                        className="border border-line px-3 py-2 mono text-xs hover:bg-red-100"
                                    >
                                        DELETE
                                    </button>
                                </div>
                            </div>

                            {experience.bullets?.length > 0 && (
                                <ul className="mt-4 border-t border-line pt-3 space-y-2">
                                    {experience.bullets.map((bullet, index) => (
                                        <li
                                            key={index}
                                            className="text-sm leading-relaxed"
                                        >
                                            • {bullet}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    </section>
)}

            <div className="fixed bottom-0 inset-x-0 bg-ink text-paper px-5 lg:px-10 py-4 flex items-center justify-between">
                <span className="mono text-[10px] tracking-[0.2em] text-paper2/60">
                    CHANGES GO LIVE ON SAVE
                </span>
                <button
                    onClick={saveAll}
                    disabled={saving}
                    data-testid="admin-save-button"
                    className="inline-flex items-center gap-2 bg-sage text-ink px-6 py-2.5 mono text-[11px] tracking-[0.25em] hover:bg-paper transition-colors disabled:opacity-60"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    SAVE ALL
                </button>
            </div>
        </div>
    );
}
